"use client";
import Logo from "./Logo";
import Link from "next/link";
import React, { useState, useRef, useEffect } from "react";
import { DropdownMenu } from "@/app/components/DropdownMenu";

interface INavLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
}

interface NavButton {
  url: string;
  newTab: boolean;
  text: string;
}

function NavLink({
  navLink,
  currentPath,
  onNavigate,
}: {
  navLink: INavLink;
  currentPath: string | null;
  onNavigate?: () => void;
}) {
  return (
    <li className="flex relative group">
      <Link
        href={navLink.url}
        onClick={onNavigate}
        className={`mb-[16px] dt:mb-0 uppercase font-bold text-[16px] leading-[16px] tracking-wider underline-offset-[4px] hover:underline ${
          currentPath === navLink.url && "underline"
        }`}
      >
        {navLink.text}
      </Link>
    </li>
  );
}

export default function Navbar({
  links,
  logoUrl,
  button,
  services,
  destinations,
  fleet,
}: {
  links: Array<INavLink>;
  services: Array<INavLink>;
  logoUrl: string | null;
  button: NavButton;
  destinations: Array<INavLink>;
  fleet: Array<INavLink>;
}) {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width:1024px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  const sc = useRef<HTMLDivElement>(null);
  const servicesLinks = services.sort((a, b) => a.text.localeCompare(b.text));
  const destinationsLinks = destinations.sort((a, b) =>
    a.text.localeCompare(b.text)
  );
  const fleetLinks = fleet.sort((a, b) => a.text.localeCompare(b.text));

  const showServices = () => {
    sc.current?.classList.remove("hidden");
  };
  const hideServices = () => {
    sc.current?.classList.add("hidden");
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenuOpen = () => {
    setMobileMenuOpen((prev) => !prev);
  };
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  const toggleServices = (url: any) => {
    if (url.url === "/services") {
      showServices();
    }
  };

  const renderMobileNavLink = (item: INavLink) => {
    if (item.url === "/destinations") {
      return (
        <DropdownMenu
          urlItem={item.url}
          title={item.text}
          data={destinationsLinks}
          onNavigate={closeMobileMenu}
        />
      );
    }

    if (item.url === "/services") {
      return (
        <DropdownMenu
          urlItem={item.url}
          title={item.text}
          data={servicesLinks}
          onNavigate={closeMobileMenu}
        />
      );
    }

    if (item.url === "/fleet") {
      return (
        <DropdownMenu
          urlItem={item.url}
          title={item.text}
          data={fleetLinks}
          onNavigate={closeMobileMenu}
        />
      );
    }

    return (
      <NavLink
        key={item.id}
        navLink={item}
        currentPath={currentPath}
        onNavigate={closeMobileMenu}
      />
    );
  };

  const filteredLinks = links.filter((link) => {
    const shouldHide =
      link.url === "/safety" ||
      link.url === "/covid" ||
      link.url === "/covid-19";

    return !shouldHide;
  });

  return isMobile ? (
    <div
      className={`fixed inset-x-0 top-0 z-30 p-[8px] dt:p-[16px] ${
        mobileMenuOpen ? "min-h-screen" : ""
      }`}
    >
      <div
        className={`h-full rounded-[8px] ${
          mobileMenuOpen ? "flex min-h-[calc(100vh-16px)] flex-col" : ""
        }`}
        id="header"
      >
        <div className="px-[18px] dt:px-[16px] py-[12px] w-full flex items-center justify-between ">
          <div className="flex flex-1">
            <Logo src={logoUrl}></Logo>
          </div>
          <button
            title="Toggle menu"
            aria-label="Toggle menu"
            className={`burger uppercase font-bold text-[16px] leading-[16px] tracking-wider  ${
              mobileMenuOpen ? "active" : ""
            }`}
            onClick={() => toggleMobileMenuOpen()}
          ></button>
        </div>

        {/* MOBILE LIST */}
        {mobileMenuOpen && (
          <div className="flex flex-1 flex-col items-center justify-center mt-[86px] pb-[32px]">
            <div className="flex flex-1 flex-col items-center relative w-[80%] overflow-y-auto nav-scrollbar">
              {filteredLinks.map((item: INavLink) => (
                <div
                  className="flex flex-col items-center relative"
                  key={item.url}
                >
                  {renderMobileNavLink(item)}
                </div>
              ))}
            </div>
            <Link
              href={button.url}
              onClick={closeMobileMenu}
              className={`bg-red hover:bg-blue text-white px-[12px] py-[8px] mt-[32px] rounded-[8px] uppercase font-bold text-[16px] leading-[16px] tracking-wider`}
            >
              {button.text}
            </Link>
          </div>
        )}
        {/* END OF MOBILE LIST */}
      </div>
    </div>
  ) : (
    <div
      className={`p-[8px] dt:p-[16px] fixed w-full z-30 ${
        mobileMenuOpen === true ? "h-screen" : ""
      }`}
    >
      <div className="h-full rounded-[8px] " id="header">
        <div className="px-[18px] dt:px-[16px] py-[12px] w-full flex items-center justify-between ">
          <div className="flex flex-1">
            <Logo src={logoUrl}></Logo>
          </div>

          {/* DESKTOP LIST */}
          <ul className="hidden dt:flex flex-1 items-center relative">
            {filteredLinks.map((item: INavLink) => (
              <div
                className="mr-[16px] flex items-center justify-center"
                key={item.url}
              >
                <span
                  onMouseEnter={() => {
                    toggleServices(item);
                  }}
                  onMouseLeave={hideServices}
                >
                  <NavLink
                    key={item.id}
                    navLink={item}
                    currentPath={currentPath}
                  />
                </span>
                {item.url === "/destinations" && (
                  <DropdownMenu data={destinationsLinks} />
                )}
                {item.url === "/services" && (
                  <DropdownMenu data={servicesLinks} />
                )}
                {item.url === "/fleet" && (
                  <DropdownMenu data={fleetLinks} />
                )}
              </div>
            ))}
          </ul>
          {/* END OF DESKTOP LIST */}

          <div className="hidden dt:flex flex-1 justify-end">
            <a
              href={button.url}
              className={`bg-red hover:bg-blue text-white px-[12px] py-[8px] rounded-[8px] uppercase font-bold text-[16px] leading-[16px] tracking-wider`}
            >
              {button.text}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
